import { useCallback, useEffect, useState } from 'react'

import { isMoment } from 'moment'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faEdit, faTrash, faTriangleExclamation } from '@fortawesome/pro-duotone-svg-icons'
import { useLocation } from 'react-router-dom/cjs/react-router-dom.min'
import { selectTranslate } from '../../reducers/LocaleReducer/LocalReducer'
import { getInternshipRequestByToken } from '../../utils/api/user'
import { isNonEmptyArray, isObject } from '../../utils'
import { EUROPEAN_DATE } from '../../utils/constants'
import { DAY, getTimezoneLessMoment } from '../../utils/momentjs'
import Loading from '../../Components/shared/Loading'
import {
  getComment, getEmail, getFirstname, getInternshipYear, getLastname, getSchoolName, getSectionAbbreviation, getSectorName, InternshipRequest
} from '../../utils/entities/internshipRequest'
import { RequestPeriod } from '../../utils/entities/requestPeriod'
import IconButton from '../../Components/antd/Buttons/IconButton'
import Mailing from '../../Components/shared/Mailing'

import { getOriginalName, getType, getTypeColor } from '../../utils/entities/internshipRequestFile'

import '../../assets/index.scss'
import { DatePicker, Divider, Popover, Tag } from 'antd'
import { faCalendar, faCheckCircle, faClipboardList, faComment, faDownload, faEnvelope, faFileLines, faGlobe, faGraduationCap, faPaperclip, faSchool, faStethoscope, faTrashRestore } from '@fortawesome/pro-solid-svg-icons'
import { approveInternshipRequestByToken, downloadInternshipRequestFileByToken, refuseInternshipRequestByToken } from '../../utils/api/internalToken'
import { HttpStatuses } from '../../utils/http'
import { getErrorBody } from '../../utils/apiHelper'
import AnonymousLanguagesMenu from '../InternshipRequestPortal/AnonymousLanguagesMenu'
import { useSelector } from 'react-redux'

const LABEL_STYLE = {
  display: 'flex',
  alignItems: 'center',
  gap: '0.4rem',
  fontSize: '0.7rem',
  fontWeight: 600,
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
  color: 'rgba(0, 0, 0, 0.45)'
}

const VALUE_BOX_STYLE = {
  border: '1px solid #f0f0f0',
  borderRadius: '0.4rem',
  padding: '0.6rem 0.75rem',
  color: 'rgba(0, 0, 0, 0.75)',
  fontWeight: 500
}

const COMMENT_BOX_STYLE = {
  backgroundColor: '#eaf4fb',
  borderLeft: '3px solid #3C8DBC',
  borderRadius: '0.4rem',
  padding: '0.75rem 1rem',
  color: 'rgba(0, 0, 0, 0.65)',
  lineHeight: 1.5
}

const PERIOD_BADGE_STYLE = {
  backgroundColor: '#eef1f5',
  borderRadius: '0.25rem',
  padding: '0.05rem 0.4rem',
  fontSize: '0.7rem',
  fontWeight: 600,
  color: 'rgba(0, 0, 0, 0.55)'
}

const AVATAR_STYLE = {
  flexShrink: 0,
  width: '2.75rem',
  height: '2.75rem',
  borderRadius: '50%',
  backgroundColor: '#5fb878',
  color: 'white',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontWeight: 600,
  fontSize: '1rem',
  textTransform: 'uppercase'
}

const PILL_STYLE = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '0.35rem',
  border: '1px solid #e0e0e0',
  borderRadius: '1rem',
  padding: '0.1rem 0.6rem',
  fontSize: '0.75rem',
  color: 'rgba(0, 0, 0, 0.55)'
}

const STATUS_PILL_STYLE = {
  display: 'inline-flex',
  alignItems: 'center',
  borderRadius: '1rem',
  padding: '0.1rem 0.6rem',
  fontSize: '0.75rem',
  fontWeight: 500,
  backgroundColor: '#fff4e5',
  color: '#d48806'
}

const supportedErrors = [
  'INVALID_TOKEN', 'EXPIRED_TOKEN', 'TOKEN_ALREADY_CONSUMED', 'INVALID_TOKEN_PAYLOAD',
  'INTERNSHIP_REQUEST_DOES_NOT_REQUIRE_SECTOR_APPROVAL_ANYMORE', 'INVALID_PAYLOAD', 'INTERNSHIP_REQUEST_DOES_NOT_EXIST',
  'INTERNSHIP_REQUEST_ALREADY_REFUSED_OR_DELETED', 'INTERNSHIP_REQUEST_ALREADY_APPROVED'
]

const { RangePicker } = DatePicker

const getInitials = (firstName, lastName) => `${firstName?.[0] ?? ''}${lastName?.[0] ?? ''}`

const SectionLabel = ({ icon, text }) => (
  <span style={LABEL_STYLE}>
    <FontAwesomeIcon icon={icon} style={{ color: '#3C8DBC' }} />
    {text}
  </span>
)

const formatPeriod = (period) => (
  `${getTimezoneLessMoment(period.startDate).format(EUROPEAN_DATE)} → ${getTimezoneLessMoment(period.endDate).format(EUROPEAN_DATE)}`
)

const DisplayIndividualInternshipRequestByToken = () => {
  const t = useSelector(selectTranslate)
  const location = useLocation()

  const [internshipRequest, setInternshipRequest] = useState(null)
  const [loading, setLoading] = useState(true)
  const [status, setStatus] = useState(null)
  const [removedPeriodIds, setRemovedPeriodIds] = useState(new Set())
  const [periodDateOverrides, setPeriodDateOverrides] = useState({})

  const params = new URLSearchParams(location.search)
  const token = params.get('token')

  const handleError = useCallback((error) => {
    const errorStatus = {
      icon: faTriangleExclamation,
      color: '#e0a800',
      title: 'get_internship_request_by_token.error.generic.title',
      body: 'get_internship_request_by_token.error.generic.body'
    }
    if (error.status === HttpStatuses.NOT_FOUND) {
      getErrorBody(error).then(({ message }) => {
        if (supportedErrors.includes(message)) {
          errorStatus.title = `get_internship_request_by_token.error.${message.toLowerCase()}.title`
          errorStatus.body = `get_internship_request_by_token.error.${message.toLowerCase()}.body`
        } else if (message === 'This InternalToken does not exist.') {
          errorStatus.title = 'get_internship_request_by_token.error.invalid_token.title'
          errorStatus.body = 'get_internship_request_by_token.error.invalid_token.body'
        }

        setStatus(errorStatus)
      })
    } else if (error.status === HttpStatuses.BAD_REQUEST) {
      getErrorBody(error).then(({ message }) => {
        if (supportedErrors.includes(message)) {
          errorStatus.title = `get_internship_request_by_token.error.${message.toLowerCase()}.title`
          errorStatus.body = `get_internship_request_by_token.error.${message.toLowerCase()}.body`
        }

        setStatus(errorStatus)
      })
    } else {
      setStatus(errorStatus)
    }
  }, [])

  useEffect(() => {
    if (token) {
      setLoading(true)
      getInternshipRequestByToken(token).then(({ data }) => {
        setInternshipRequest(new InternshipRequest(data))
      }).catch(error => {
        handleError(error)
        setInternshipRequest(null)
      }).finally(() => setLoading(false))
    } else {
      setInternshipRequest(null)
      setStatus({
        title: 'get_internship_request_by_token.error.no_token_provided.title',
        body: 'get_internship_request_by_token.error.no_token_provided.body'
      })
    }
  }, [token, handleError])

  const requestPeriods = internshipRequest?.requestPeriods ?? []
  const hasPeriods = isNonEmptyArray(requestPeriods)
  const canApprove = !hasPeriods || requestPeriods.some(period => !removedPeriodIds.has(period.id))

  const handleFileDownload = file => {
    downloadInternshipRequestFileByToken(token, file).catch(handleError)
  }

  const handleRemovePeriod = periodId => {
    setRemovedPeriodIds(previous => new Set(previous).add(periodId))
  }

  const handleRestorePeriod = periodId => {
    setRemovedPeriodIds(previous => {
      const next = new Set(previous)
      next.delete(periodId)

      return next
    })
  }

  const getPeriodDates = period => periodDateOverrides[period.id] ?? period

  const handlePeriodDatesChange = (period, dates) => {
    const [startDate, endDate] = dates ?? []

    if (!isMoment(startDate) || !isMoment(endDate)) {
      return
    }

    setPeriodDateOverrides(previous => ({ ...previous, [period.id]: { startDate, endDate } }))
  }

  const handleResetPeriodDates = periodId => {
    setPeriodDateOverrides(previous => {
      const next = { ...previous }
      delete next[periodId]

      return next
    })
  }

  const buildApprovedPeriods = () => requestPeriods
    .filter(period => !removedPeriodIds.has(period.id))
    .map(period => {
      const { startDate, endDate } = getPeriodDates(period)

      return new RequestPeriod({ id: period.id, startDate: startDate.clone(), endDate: endDate.clone() }).toRequestBody()
    })

  const handleInternshipRequest = reject => {
    const promise = reject
      ? refuseInternshipRequestByToken(token)
      : approveInternshipRequestByToken(token, hasPeriods ? buildApprovedPeriods() : undefined)

    promise.then(() => {
      setInternshipRequest(null)
      setStatus({
        icon: faCheckCircle,
        color: '#52c41a',
        title: reject
          ? 'display_individual_internship_by_token.internship_request_rejected'
          : 'display_individual_internship_by_token.internship_request_approved',
        body: 'display_individual_internship_by_token.generic_success_message'
      })
    }).catch(error => {
      handleError(error)
    })
  }

  return (
    <div className='flex-col jc-ctr ai-ctr' style={{ height: '100vh', backgroundColor: '#b99e8d' }}>
      <div className='flex-row w-100 jc-between' style={{ position: 'absolute', top: '1rem', right: '1rem' }}>
        <div style={{ marginLeft: '2rem', width: '100px' }}> <img src='/assets/interneo_logo.svg' alt='interneo-logo' /> </div>
        <AnonymousLanguagesMenu icon={faGlobe} />
      </div>

      <div className='flex-col' style={{ backgroundColor: 'white', borderRadius: '0.5rem', width: '90%', maxWidth: '50rem' }}>
        {isObject(status) && (
          <div className='flex-col ai-ctr pd-16' style={{ textAlign: 'center', gap: '1rem', width: '100%' }}>
            <FontAwesomeIcon icon={status.icon} style={{ fontSize: '3rem', color: status.color ?? '#e0a800' }} />
            <h2 style={{ margin: 0, width: '100%' }}>{t(status.title)}</h2>
            <p style={{ margin: 0, width: '100%', lineHeight: 1.5, color: 'rgba(0, 0, 0, 0.55)' }}>
              {status.body && t(status.body)}
            </p>
          </div>
        )}
        {token && loading && <div className='pd-16'> <Loading size='3x' /></div>}
        {isObject(internshipRequest) && !loading && (
          <div className='internship-request-card'>
            <div className='internship-request-title pd-16 flex-row ai-ctr'>
              <h3 className='flex-row m-0' style={{ color: 'rgba(0, 0, 0, 0.65)' }}>
                <FontAwesomeIcon icon={faClipboardList} style={{ fontSize: '1.4rem', color: '#3C8DBC' }} />
                <div className='ml-8' />
                <b>{t('common.individual_internship_request')}</b>
              </h3>
            </div>
            <Divider style={{ width: 'auto', margin: '0px' }} type='horizontal' />
            <div className='internship-request-student flex-row ai-ctr pd-16' style={{ gap: '0.75rem' }}>
              <div style={AVATAR_STYLE}>{getInitials(getFirstname(internshipRequest), getLastname(internshipRequest))}</div>
              <div className='flex-col' style={{ gap: '0.4rem', minWidth: 0 }}>
                <b style={{ fontSize: '1.05rem', color: 'rgba(0, 0, 0, 0.75)' }}>
                  {`${getFirstname(internshipRequest) ?? ''} ${getLastname(internshipRequest) ?? ''}`.trim()}
                </b>
                {getEmail(internshipRequest) && (
                  <span className='flex-row ai-ctr' style={{ gap: '0.4rem', fontSize: '0.8rem', color: 'rgba(0, 0, 0, 0.55)' }}>
                    <FontAwesomeIcon icon={faEnvelope} />
                    <Mailing email={getEmail(internshipRequest)} />
                  </span>
                )}
                <div className='flex-row ai-ctr' style={{ gap: '0.5rem', flexWrap: 'wrap' }}>
                  {[getSectionAbbreviation(internshipRequest), getInternshipYear(internshipRequest)].filter(Boolean).length > 0 && (
                    <span style={PILL_STYLE}>
                      <FontAwesomeIcon icon={faGraduationCap} />
                      {[getSectionAbbreviation(internshipRequest), getInternshipYear(internshipRequest)].filter(Boolean).join(' · ')}
                    </span>
                  )}
                  <span style={STATUS_PILL_STYLE}>
                    {t('display_individual_internship_by_token.status.pending_approval')}
                  </span>
                </div>
              </div>
            </div>
            <Divider style={{ width: 'auto', margin: '0px' }} type='horizontal' />
            <div className='internship-request-body flex-col pd-16' style={{ gap: '1rem' }}>
              <div className='flex-row' style={{ gap: '1rem', flexWrap: 'wrap' }}>
                <div className='flex-col' style={{ ...VALUE_BOX_STYLE, gap: '0.4rem', flex: '1 1 14rem', minWidth: 0 }}>
                  <SectionLabel icon={faStethoscope} text={t('common.sector')} />
                  {getSectorName(internshipRequest) ?? t('common.unknown_sector')}
                </div>
                <div className='flex-col' style={{ ...VALUE_BOX_STYLE, gap: '0.4rem', flex: '1 1 14rem', minWidth: 0 }}>
                  <SectionLabel icon={faCalendar} text={t('display_individual_internship_by_token.body.periods')} />
                  {hasPeriods
                    ? requestPeriods.map((period, index) => {
                      const dates = getPeriodDates(period)
                      const isRemoved = removedPeriodIds.has(period.id)
                      const isEdited = periodDateOverrides[period.id] !== undefined

                      return (
                        <div
                          key={period.id ?? index}
                          className='flex-row ai-ctr'
                          style={{ gap: '0.5rem', flexWrap: 'wrap', opacity: isRemoved ? 0.45 : 1 }}
                        >
                          <span style={PERIOD_BADGE_STYLE}>P{index + 1}</span>
                          <span style={{ color: '#3C8DBC', fontWeight: 600, textDecoration: isRemoved ? 'line-through' : 'none' }}>
                            {formatPeriod(dates)}
                          </span>
                          <Popover
                            trigger='click'
                            placement='bottom'
                            title={t('display_individual_internship_by_token.body.edit_period_title')}
                            content={
                              <div className='flex-col' style={{ gap: '0.5rem' }}>
                                <RangePicker
                                  allowClear={false}
                                  format={EUROPEAN_DATE}
                                  value={[dates.startDate, dates.endDate]}
                                  disabledDate={date => date.isBefore(period.startDate, DAY) || date.isAfter(period.endDate, DAY)}
                                  onChange={selectedDates => handlePeriodDatesChange(period, selectedDates)}
                                />
                                {isEdited && (
                                  <IconButton
                                    size='small'
                                    text={t('common.reset')}
                                    onClick={() => handleResetPeriodDates(period.id)}
                                  />
                                )}
                              </div>
                            }
                          >
                            <IconButton
                              type='primary'
                              size='small'
                              icon={faEdit}
                              disabled={isRemoved}
                              title={t('display_individual_internship_by_token.body.edit_period_title')}
                            />
                          </Popover>
                          {isRemoved
                            ? (
                              <IconButton
                                size='small'
                                icon={faTrashRestore}
                                title={t('display_individual_internship_by_token.body.restore_period')}
                                onClick={() => handleRestorePeriod(period.id)}
                              />
                            )
                            : (
                              <IconButton
                                type='danger'
                                size='small'
                                icon={faTrash}
                                title={t('display_individual_internship_by_token.body.remove_period')}
                                onClick={() => handleRemovePeriod(period.id)}
                              />
                            )}
                        </div>
                      )
                    })
                    : <span style={{ color: 'rgba(0, 0, 0, 0.45)' }}>—</span>}
                </div>
              </div>
              <div className='flex-col' style={{ ...VALUE_BOX_STYLE, gap: '0.4rem' }}>
                <SectionLabel icon={faSchool} text={t('common.school')} />
                {getSchoolName(internshipRequest) ?? '—'}
              </div>
              {getComment(internshipRequest) && (
                <div className='flex-col' style={{ ...COMMENT_BOX_STYLE, gap: '0.4rem' }}>
                  <SectionLabel icon={faComment} text={t('display_individual_internship_by_token.body.comment')} />
                  {getComment(internshipRequest)}
                </div>
              )}
              {isNonEmptyArray(internshipRequest.files) && (
                <div className='flex-col' style={{ ...VALUE_BOX_STYLE, gap: '0.5rem' }}>
                  <SectionLabel icon={faPaperclip} text={t('display_individual_internship_by_token.body.files')} />
                  {internshipRequest.files.map((file, index) => (
                    <div key={file.id ?? index} className='flex-row ai-ctr jc-between' style={{ gap: '0.5rem', flexWrap: 'wrap' }}>
                      <span className='flex-row ai-ctr' style={{ gap: '0.5rem', minWidth: 0 }}>
                        <FontAwesomeIcon icon={faFileLines} style={{ color: '#3C8DBC' }} />
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{getOriginalName(file)}</span>
                      </span>
                      <span className='flex-row ai-ctr' style={{ gap: '0.5rem' }}>
                        <Tag color={getTypeColor(file)} style={{ margin: 0 }}>
                          {t('internship_request_file.types.' + getType(file).toLowerCase())}
                        </Tag>
                        <IconButton
                          size='small'
                          icon={faDownload}
                          iconStyle={{ color: '#3C8DBC' }}
                          title={t('common.download')}
                          onClick={() => handleFileDownload(file)}
                        />
                      </span>
                    </div>

                  ))}
                </div>
              )}
            </div>
            <Divider style={{ width: 'auto', margin: '0px' }} type='horizontal' />
            <div className='internship-request-footer flex-row ai-ctr jc-ctr pd-16' style={{ gap: '0.5rem', flexWrap: 'wrap' }}>
              <IconButton
                type='danger'
                disabled={loading}
                text={t('common.reject')}
                style={{ flex: '1 1 10rem' }}
                onClick={() => handleInternshipRequest(true)}
              />
              <IconButton
                type='primary'
                disabled={loading || !canApprove}
                text={t('common.approved')}
                style={{ flex: '1 1 10rem' }}
                onClick={() => handleInternshipRequest(false)}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default DisplayIndividualInternshipRequestByToken
